import { createEffect, createSignal, onCleanup, onMount, Show } from 'solid-js'
import { A } from '@solidjs/router'
import AccountSidebar from './AccountSidebar'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import Footer from '/web/shared/aifans/Footer'
import { chatStore, toastStore, userStore } from '/web/store'
import { AIFansSubscription } from '/common/types/aifans-schema'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { getModelUrl } from '/web/shared/aifans/lib/model-util'
import SubscriptionModal from './SubscriptionModal'
import CancellationModal from './CancellationModal'

export default function Subscriptions() {
  const [toggleOption, setToggleOption] = createSignal('all')
  const [showCancelModal, setShowCancelModal] = createSignal(false)
  const [showReactivateModal, setShowReactivateModal] = createSignal(false)
  const [cancelURL, setCancelURL] = createSignal<string>()
  const [reactivateURL, setReactivateURL] = createSignal<string>()
  const [cancellingId, setCancellingId] = createSignal<string>()
  const [showCancellationModal, setShowCancellationModal] = createSignal(false)
  const [cancellingCharId, setCancellingCharId] = createSignal<string>()

  // Signal to track data and current sorting state
  const [tableData, setTableData] = createSignal(userStore().aifansSubscriptions || [])
  const [sortKey, setSortKey] = createSignal<keyof AIFansSubscription | null>(null)
  const [sortDirection, setSortDirection] = createSignal<'asc' | 'desc'>('asc')

  createEffect(async () => {
    await loadSubscriptions()
  })

  createEffect(async () => {
    if (toggleOption() === 'all') {
      filterData(userStore().aifansSubscriptions)
    } else if (toggleOption() === 'active') {
      filterData(userStore().aifansSubscriptions?.filter((sub) => sub.status === 'active'))
    } else if (toggleOption() === 'expired') {
      filterData(userStore().aifansSubscriptions?.filter((sub) => sub.status === 'cancelled'))
    }
  })

  onMount(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'subscription-cancelled') {
        setShowCancelModal(false)
        toastStore.success('Subscription cancelled!')
        loadSubscriptions()
      } else if (event.data.type === 'subscription-reactivated') {
        setShowReactivateModal(false)
        toastStore.success('Subscription reactivated!')
        loadSubscriptions()
      }
    }

    window.addEventListener('message', handleMessage)
    onCleanup(() => window.removeEventListener('message', handleMessage))
  })

  const loadSubscriptions = async () => {
    await userStore.loadAIFansSubscriptions()
    filterData(userStore().aifansSubscriptions)
    setToggleOption('all')
  }

  const filterData = (data: AIFansSubscription[] | undefined) => {
    if (data && sortKey()) {
      const sorted = getSortedData(data, sortKey()!, sortDirection())
      setTableData(sorted)
    } else if (data) {
      setTableData(data)
      sortData('startDate', 'desc')
    }
  }

  const characterFromId = (characterId: string) => {
    const char = chatStore().allChars.list.find((c) => c._id === characterId)
    return char
  }

  const cancelSubscription = async (characterId: string) => {
    setCancellingCharId(characterId)
    setShowCancellationModal(true)
  }

  const handleCancellationConfirm = async (reason: string) => {
    const characterId = cancellingCharId()
    if (!characterId) return

    setCancellingId(characterId)
    try {
      const res = await aifansSubscriptionApi.createCancellationLink(characterId, reason)
      if (res.result?.success) {
        await loadSubscriptions()
        toastStore.success('Subscription cancelled!')
      } else {
        throw new Error('Failed to cancel subscription with Epoch.')
      }
    } catch (error) {
      console.log(error)
      toastStore.error('There was a problem canceling your subscription. Please try again.')
    } finally {
      setCancellingId(undefined)
      setCancellingCharId(undefined)
      setShowCancellationModal(false)
    }
  }

  const reactivateSubscription = async (characterId: string) => {
    if (
      confirm(
        `Are you sure you want to reactivate your subscription to ${
          characterFromId(characterId)?.name
        }?`
      )
    ) {
      try {
        const res = await aifansSubscriptionApi.createReactivationLink(characterId)
        if (res.result?.redirectURL) {
          setReactivateURL(res.result.redirectURL)
          setShowReactivateModal(true)
        } else {
          throw new Error('No redirect URL received')
        }
      } catch (error) {
        console.log(error)
        toastStore.error(
          'There was a problem reactivating your subscription. If you have already finished reactivating your subscription you may need to wait up to 15 minutes for reactivation to be completed. If not please try again or contact support.'
        )
      }
    }
  }

  // Sorting logic
  const sortData = (key: keyof AIFansSubscription, initialDirection?: 'asc' | 'desc') => {
    if (sortKey() === key) {
      setSortDirection(sortDirection() === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection(initialDirection || 'asc')
    }

    const sorted = getSortedData(tableData() ?? [], key, sortDirection())
    setTableData(sorted)
  }

  const getSortedData = (
    data: AIFansSubscription[],
    key: keyof AIFansSubscription,
    direction: 'asc' | 'desc'
  ) => {
    return [...data].sort((a, b) => {
      let aValue: any = a[key]
      let bValue: any = b[key]

      if (key === 'characterId') {
        aValue = characterFromId(a.characterId)?.name
        bValue = characterFromId(b.characterId)?.name
      }

      if (key === 'amount') {
        aValue = parseFloat(a.amount.toString())
        bValue = parseFloat(b.amount.toString())
        if (isNaN(aValue)) aValue = Infinity
        if (isNaN(bValue)) bValue = Infinity
      }

      if (key === 'startDate' || key === 'endDate') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }

      if (key === 'status') {
        aValue = a.status
        bValue = b.status
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1
      if (aValue > bValue) return direction === 'asc' ? 1 : -1
      return 0
    })
  }

  // Helper to render sorting indicator
  const renderSortIndicator = (key: keyof AIFansSubscription) => {
    if (sortKey() === key) {
      return <div class="text-xxs">{sortDirection() === 'asc' ? ' ▲' : ' ▼'}</div>
    }
    return ''
  }

  const getStatus = (subscription: AIFansSubscription) => {
    switch (subscription.status) {
      case 'active':
        return { text: 'Active', class: 'text-cosplay-green' }
      case 'cancelled':
        return { text: 'Cancelled', class: 'text-red' }
      case 'expired':
        return { text: 'Expired', class: 'text-red' }
      case 'reactivating':
        return {
          text: 'Reactivating...',
          class: 'text-yellow-400',
          subtext: {
            text: 'Try again',
            action: () => reactivateSubscription(subscription.characterId),
          },
        }
      case 'restarted':
        return { text: 'Restarted', class: 'text-gray-400' }
      default:
        return { text: 'Unknown', class: 'text-gray-400' }
    }
  }

  return (
    <>
      <HeaderContainer />

      <main class="subscriptions page-bg relative overflow-hidden bg-main pb-20 pt-20 sm:pt-28 md:bg-none md:pb-36">
        <div class="container relative z-20">
          <div class="wrapper gap-8 lg:flex">
            <AccountSidebar />
            {/* right */}
            <div class="right flex w-full flex-col">
              {/* head */}
              <div class="head gray-to-tran mb-3 hidden rounded-xl text-2xl font-semibold sm:mb-7 sm:block sm:p-4 sm:text-[28px]">
                <h2>Subscriptions</h2>
              </div>
              <div class="gray-to-tran h-full w-full rounded-xl sm:p-6">
                <h2 class="title px-4 py-3 text-2xl font-semibold sm:hidden">Subscriptions</h2>

                <div class="top flex flex-col justify-between gap-4 px-4 py-3 sm:flex-row">
                  <div class="toggle left">
                    <ul class="flex gap-2.5 text-base xs:gap-4 sm:gap-6 sm:text-xl">
                      <li
                        onClick={() => setToggleOption('all')}
                        class={`${toggleOption() === 'all' && '!border-white/60'}`}
                      >
                        All
                      </li>
                      <li
                        onClick={() => setToggleOption('active')}
                        class={`${toggleOption() === 'active' && '!border-white/60'}`}
                      >
                        Active
                      </li>
                      <li
                        onClick={() => setToggleOption('expired')}
                        class={`${toggleOption() === 'expired' && '!border-white/60'}`}
                      >
                        Expired
                      </li>
                    </ul>
                  </div>
                </div>

                {/* payment table */}
                <div class="mt-6 inline-block w-full overflow-x-auto font-normal">
                  {tableData()?.length > 0 ? (
                    <>
                      {/* desktop */}

                      <div class="desktop hidden sm:block">
                        <table class="w-full table-auto">
                          <thead>
                            <tr>
                              <th onClick={() => sortData('characterId')}>
                                <div class=" space-between flex cursor-pointer items-center gap-2">
                                  Creator {renderSortIndicator('characterId')}
                                </div>
                              </th>
                              <th onClick={() => sortData('amount')}>
                                <div class=" space-between flex cursor-pointer items-center gap-2">
                                  Amount {renderSortIndicator('amount')}
                                </div>
                              </th>
                              <th onClick={() => sortData('startDate')}>
                                <div class=" space-between flex cursor-pointer items-center gap-2">
                                  Subscribed {renderSortIndicator('startDate')}
                                </div>
                              </th>
                              <th onClick={() => sortData('endDate')}>
                                <div class=" space-between flex cursor-pointer items-center gap-2">
                                  {' '}
                                  Next Rebill {renderSortIndicator('endDate')}
                                </div>
                              </th>
                              <th onClick={() => sortData('status')}>
                                <div class=" space-between flex cursor-pointer items-center gap-2">
                                  Status {renderSortIndicator('status')}
                                </div>
                              </th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableData()?.map((item, i) => (
                              <tr
                                class={i !== 0 ? 'border-t border-gray-700' : ''}
                                data-subscription-id={item._id}
                              >
                                <td data-character-id={item.characterId}>
                                  <A
                                    href={getModelUrl(
                                      characterFromId(item.characterId)?.categorySlug,
                                      characterFromId(item.characterId)?.slug
                                    )}
                                    class="text-[#10e0f9] hover:underline"
                                  >
                                    <div class="text-base">
                                      {characterFromId(item.characterId)?.name}
                                    </div>
                                    {characterFromId(item.characterId)?.realName && (
                                      <div class="text-xs text-gray-400">
                                        {characterFromId(item.characterId)?.realName}
                                      </div>
                                    )}
                                  </A>
                                </td>
                                <td data-subscription-id={item._id}>
                                  <span class="font-medium text-[#10e0f9]">
                                    {`\$${item.amount}` || 'N/A'}
                                  </span>
                                </td>
                                <td>{formatDate(item.startDate)}</td>
                                <td>{formatDate(item.endDate)}</td>
                                <td class={getStatus(item).class}>
                                  {getStatus(item).text}
                                  {getStatus(item).subtext && (
                                    <div class="text-xs">
                                      <button
                                        class="text-cyan hover:underline"
                                        onClick={getStatus(item).subtext?.action}
                                      >
                                        {getStatus(item)?.subtext?.text}
                                      </button>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <Show when={getStatus(item).text === 'Active'}>
                                    <button
                                      class="text-cosplay-red"
                                      onClick={() => cancelSubscription(item.characterId)}
                                      disabled={cancellingId() === item.characterId}
                                    >
                                      {cancellingId() === item.characterId ? (
                                        <span class="flex items-center gap-2">
                                          <div class="h-4 w-4 animate-spin rounded-full border-2 border-cosplay-red border-t-transparent"></div>
                                          Cancelling...
                                        </span>
                                      ) : (
                                        'Cancel'
                                      )}
                                    </button>
                                  </Show>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* mobile */}
                      <div class="mobile-subscriptions grid gap-4 sm:hidden">
                        {tableData()?.map((item) => (
                          <div class="subscription-item grid grid-cols-[1fr,auto] gap-4 border-b border-gray-700 pb-4">
                            {/* Left column */}
                            <div class="flex flex-col gap-1">
                              <A
                                href={getModelUrl(
                                  characterFromId(item.characterId)?.categorySlug,
                                  characterFromId(item.characterId)?.slug
                                )}
                                class="text-[#10e0f9] hover:underline"
                              >
                                {characterFromId(item.characterId)?.name}
                              </A>
                              {characterFromId(item.characterId)?.realName && (
                                <div class="text-sm text-gray-400">
                                  {characterFromId(item.characterId)?.realName}
                                </div>
                              )}
                              <div class="mt-1 space-y-0.5 text-sm text-gray-300">
                                <div>
                                  Amount:{' '}
                                  <span class="font-medium text-[#10e0f9]">${item.amount}</span>
                                </div>
                                <div>Subscribed: {formatDate(item.startDate)}</div>
                                <div>Next Rebill: {formatDate(item.endDate)}</div>
                              </div>
                            </div>

                            {/* Right column */}
                            <div class="flex flex-col items-end gap-2">
                              <div class={`text-right ${getStatus(item).class}`}>
                                {getStatus(item).text}
                              </div>
                              {getStatus(item).subtext && (
                                <button
                                  class="text-sm text-cyan hover:underline"
                                  onClick={getStatus(item).subtext?.action}
                                >
                                  {getStatus(item).subtext?.text}
                                </button>
                              )}
                              <Show when={getStatus(item).text === 'Active'}>
                                <button
                                  class="text-sm text-cosplay-red hover:underline"
                                  onClick={() => cancelSubscription(item.characterId)}
                                  disabled={cancellingId() === item.characterId}
                                >
                                  {cancellingId() === item.characterId ? (
                                    <span class="flex items-center gap-2">
                                      <div class="h-3 w-3 animate-spin rounded-full border-2 border-cosplay-red border-t-transparent"></div>
                                      Cancelling...
                                    </span>
                                  ) : (
                                    'Cancel'
                                  )}
                                </button>
                              </Show>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div class="empty-state py-8 text-center">
                      <p class="text-xl text-gray-400">No subscriptions found.</p>
                      <p class="mt-2 text-sm text-gray-500">
                        Your active and past subscriptions will appear here once you subscribe to a
                        creator.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* bg */}
        <div class="radial-bg bottom-[-40%] left-[-250px] right-0 h-[1000px] w-[900px] opacity-10 md:hidden"></div>
      </main>
      <Footer />
      <SubscriptionModal
        show={showCancelModal() || showReactivateModal()}
        onClose={() => {
          setShowCancelModal(false)
          setCancelURL(undefined)
          setShowReactivateModal(false)
          setReactivateURL(undefined)
        }}
        redirectURL={cancelURL() || reactivateURL()}
      />
      <CancellationModal
        show={showCancellationModal()}
        onClose={() => {
          setShowCancellationModal(false)
          setCancellingCharId(undefined)
        }}
        onConfirm={handleCancellationConfirm}
        characterName={cancellingCharId() ? characterFromId(cancellingCharId()!)?.name : undefined}
        loading={cancellingId() === cancellingCharId()}
      />
    </>
  )
}

const formatDate = (dateString: string) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateString))
}
